import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface Course {
  id: string;
  name: string;
  friendlyId: string;
  created: string;
  studentCount: number;
  instructorCount: number;
}

interface CourseListProps {
  isExpanded: boolean;
  loading: boolean;
  courses: Course[];
  onToggleExpand: () => void;
}

export function CourseList({
  isExpanded,
  loading,
  courses,
  onToggleExpand,
}: CourseListProps) {
  const handleExportCSV = () => {
    const headers = [
      "Course Name",
      "Course ID",
      "First Launch Date",
      "Students",
      "Instructors",
    ];
    const rows = courses.map((course) => [
      course.name,
      course.friendlyId,
      new Date(course.created).toLocaleDateString(),
      course.studentCount,
      course.instructorCount,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `course_metrics_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper sx={{ mt: 4, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Active Courses</Typography>
        <Box>
          {isExpanded && (
            <IconButton onClick={handleExportCSV} sx={{ mr: 1 }}>
              <FileDownloadIcon />
            </IconButton>
          )}
          <IconButton onClick={onToggleExpand}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Course Name</TableCell>
                <TableCell>Course ID</TableCell>
                <TableCell>First Launch Date</TableCell>
                <TableCell align="right">Students</TableCell>
                <TableCell align="right">Instructors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : courses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No courses found
                  </TableCell>
                </TableRow>
              ) : (
                courses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>{course.friendlyId}</TableCell>
                    <TableCell>
                      {new Date(course.created).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">{course.studentCount}</TableCell>
                    <TableCell align="right">
                      {course.instructorCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
}
